import React, { useEffect, useState } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  CircularProgress,
  Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../hooks";
import { showSnackbar } from "../../reducers/snackbarSlice";
import { getOverview, Overview } from "./OverviewRepository";

const TotalCard: React.FC<{ label: string; value: number; cy: string }> = ({ label, value, cy }) => (
  <Card elevation={2}>
    <CardContent>
      <Typography variant="h3" color="primary" data-cy={cy}>
        {value}
      </Typography>
      <Typography variant="subtitle1" color="textSecondary">
        {label}
      </Typography>
    </CardContent>
  </Card>
);

const OverviewPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let active = true;
    getOverview()
      .then((data) => {
        if (active) setOverview(data);
      })
      .catch((error) => {
        console.error(error);
        dispatch(showSnackbar({ message: t("overview.load_failed"), severity: "error" }));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [dispatch, t]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totals = overview?.totals;
  const companies = overview?.companies ?? [];

  return (
    <Box data-cy="overview-page">
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t("overview.title")}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <TotalCard label={t("overview.total_companies")} value={totals?.companies ?? 0} cy="total-companies" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TotalCard label={t("overview.total_offices")} value={totals?.offices ?? 0} cy="total-offices" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TotalCard label={t("overview.total_coaches")} value={totals?.coaches ?? 0} cy="total-coaches" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TotalCard label={t("overview.total_applicants")} value={totals?.applicants ?? 0} cy="total-applicants" />
        </Grid>
      </Grid>

      <TableContainer component={Paper} elevation={2}>
        <Table data-cy="overview-companies-table">
          <TableHead>
            <TableRow>
              <TableCell>{t("overview.company_name")}</TableCell>
              <TableCell align="right">{t("overview.office_count")}</TableCell>
              <TableCell align="right">{t("overview.coach_count")}</TableCell>
              <TableCell align="right">{t("overview.applicant_count")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>{t("overview.no_companies")}</TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <TableRow key={company.id} data-cy="overview-company-row">
                  <TableCell>{company.name}</TableCell>
                  <TableCell align="right">{company.officeCount}</TableCell>
                  <TableCell align="right">{company.coachCount}</TableCell>
                  <TableCell align="right">{company.applicantCount}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OverviewPage;
